import React, { useEffect, useState } from "react";
import axios from "axios";
import SearchBar from "../components/SearchBar/SearchBar";
import Table from "../components/Table/Table";

const Search = () => {
  const [headers] = useState([
    { dbParam: "serial_number", displayName: "S.No" },
    { dbParam: "supplier_name", displayName: "Supplier Name" },
    { dbParam: "Pmax", displayName: "Pmax" },
    { dbParam: "Vmp", displayName: "Vmpp" },
    { dbParam: "Imp", displayName: "Imp" },
    { dbParam: "Voc", displayName: "Voc" },
    { dbParam: "Isc", displayName: "Isc" },
    { dbParam: "Junction Box", displayName: "Junction Box" },
    { dbParam: "Line", displayName: "Line Code" },
    { dbParam: "FF", displayName: "FF" },
    { dbParam: "EL_Upload", displayName: "EL Upload (Yes/No)" },
    { dbParam: "Flash_Date", displayName: "Flash Date" },
    { dbParam: "Module_No", displayName: "Module Serial No" },
    { dbParam: "Model", displayName: "Model" },
    { dbParam: "Pallet_No", displayName: "Pallet No" },
    { dbParam: "Container_No", displayName: "Container No" },
    { dbParam: "Truck_No", displayName: "Truck No" },
    { dbParam: "Location", displayName: "Storage Location" },
    { dbParam: "Binning", displayName: "Binning" },
    { dbParam: "Efficiency (%)", displayName: "Efficiency (%)" },
    { dbParam: "project_id", displayName: "Project ID" },
    { dbParam: "updated_time", displayName: "Updated At" },
    { dbParam: "updated_by", displayName: "Updated By" },
  ]);

  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [zipUrl, setZipUrl] = useState(null);
  const [singleImageUrl, setSingleImageUrl] = useState(null); // NEW: Single image
  const [selectedOption, setSelectedOption] = useState("module");
  const [query, setQuery] = useState("");
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const handleSearch = async (option, value) => {
    setSelectedOption(option);
    setQuery(value);
    setIsLoading(true);
    setErrorMessage(null);
    setZipUrl(null);
    setSingleImageUrl(null); // Reset single image

    const searchType =
      option === "module" ? "module_sr_no" :
      option === "pallet" ? "pallet_no" : "container_no";

    const projectId = localStorage.getItem("selectedProject");

    try {
      // 1. Fetch table data
      const resp = await axios.post(`${baseURL}search`, {
        project_id: projectId,
        search_type: searchType,
        search_value: value,
      });

      let rows = [];
      if (resp.data.status === "success") {
        if (resp.data.documents) {
          rows = resp.data.documents.map((d) => d.data);
        } else if (resp.data.data) {
          rows = resp.data.data;
        }

        rows = rows.map((r, i) => ({
          ...r,
          serial_number: i + 1,
          ...Object.fromEntries(
            Object.entries(r).map(([k, v]) => [k, v === null ? "N/A" : v])
          ),
        }));
      } else {
        setErrorMessage(resp.data.message || "No data found");
      }
      setFilteredData(rows);

      // 2. Handle images based on search type
      if (option === "module" && rows.length > 0) {
        // SINGLE MODULE: Fetch single EL image
        if (rows[0].EL_Upload === "Yes") {
          try {
            const imgResp = await axios.post(
              `${baseURL}search?download_zip=1`, // Reuse same endpoint
              {
                project_id: projectId,
                search_type: "module_sr_no",
                search_value: value,
              },
              { responseType: "blob" }
            );
            const blob = new Blob([imgResp.data], { type: "image/jpeg" });
            setSingleImageUrl(URL.createObjectURL(blob));
          } catch (imgErr) {
            console.warn("Single image not available:", imgErr);
            setSingleImageUrl(null);
          }
        }
      } else if (option === "pallet" && rows.length > 0) {
        // PALLET: Fetch ZIP
        try {
          const zipResp = await axios.post(
            `${baseURL}search?download_zip=1`,
            {
              project_id: projectId,
              search_type: "pallet_no",
              search_value: value,
            },
            { responseType: "blob" }
          );
          const blob = new Blob([zipResp.data], { type: "application/zip" });
          setZipUrl(URL.createObjectURL(blob));
        } catch (zipErr) {
          console.warn("ZIP not available:", zipErr);
          setZipUrl(null);
        }
      }
    } catch (err) {
      console.error("Search error:", err);
      setErrorMessage(
        err.response?.data?.message || "Failed to fetch data"
      );
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (col) => {
    if (col === "serial_number") return;

    const sorted = [...filteredData].sort((a, b) => {
      const av = a[col] === "N/A" ? "" : a[col];
      const bv = b[col] === "N/A" ? "" : b[col];
      return av < bv ? -1 : av > bv ? 1 : 0;
    });

    setFilteredData(sorted.map((r, i) => ({ ...r, serial_number: i + 1 })));
  };

  const downloadZip = () => {
    if (!zipUrl) return;
    const a = document.createElement("a");
    a.href = zipUrl;
    a.download = `EL_images_pallet_${query}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadSingleImage = () => {
    if (!singleImageUrl) return;
    const a = document.createElement("a");
    a.href = singleImageUrl;
    a.download = `EL_image_${query}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="mt-5 space-y-10">
      {/* Header */}
      <div className="w-full text-center border-b-2 pb-2">
        <div className="font-semibold" style={{ color: "#808080" }}>
          Traceability (Sr #/ Pallet #/ Container)
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar onSearch={handleSearch} />

      {/* Loading */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center h-64">
          <img src="/loader.gif" alt="Loading…" className="w-28 h-28" />
          <p className="text-gray-500 mt-2">Loading data…</p>
        </div>
      )}

      {/* Error Message */}
      {!isLoading && errorMessage && (
        <div className="flex justify-center mb-5">
          <div className="flex items-center bg-red-100 px-4 py-2 rounded-lg shadow-md">
            <svg
              className="w-5 h-5 text-red-500 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" strokeWidth="2" />
              <path d="M12 8v4" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="16" r="1" fill="currentColor" />
            </svg>
            <p className="text-red-500 italic">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* SINGLE IMAGE DISPLAY + DOWNLOAD (Module only) */}
      {!isLoading && selectedOption === "module" && singleImageUrl && (
        <div className="flex flex-col items-center mb-5">
          <img
            src={singleImageUrl}
            alt="EL Image"
            className="max-w-4xl w-full h-auto rounded-lg shadow-lg"
          />
          <button
            onClick={downloadSingleImage}
            className="mt-4 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
          >
            Download EL Image
          </button>
        </div>
      )}

      {/* ZIP DOWNLOAD BUTTON (Pallet only) */}
      {!isLoading && selectedOption === "pallet" && zipUrl && (
        <div className="flex justify-center mb-5">
          <button
            onClick={downloadZip}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
          >
            📦 Download EL Images ZIP ({filteredData.length} images)
          </button>
        </div>
      )}

      {/* No Image/ZIP Message */}
      {!isLoading && query && !errorMessage && (
        <>
          {selectedOption === "module" && filteredData.length > 0 && filteredData[0]?.EL_Upload === "No" && (
            <div className="flex justify-center mb-5">
              <p className="text-yellow-600 italic bg-yellow-50 px-4 py-2 rounded-lg">
                No EL image available for this module
              </p>
            </div>
          )}
          {selectedOption === "pallet" && !zipUrl && filteredData.length > 0 && (
            <div className="flex justify-center mb-5">
              <p className="text-yellow-600 italic bg-yellow-50 px-4 py-2 rounded-lg">
                No EL images found in this pallet
              </p>
            </div>
          )}
        </>
      )}

      {/* No Data Message */}
      {!isLoading && filteredData.length === 0 && query && !errorMessage && (
        <div className="text-center text-red-500 italic mb-5">
          No data found for {selectedOption === "module" ? "Module Serial No" : selectedOption === "pallet" ? "Pallet No" : "Container No"}: <strong>{query}</strong>
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <Table
          tableHeaders={headers}
          tableData={filteredData}
          onSort={handleSort}
        />
      )}
    </div>
  );
};

export default Search;



// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import SearchBar from "../components/SearchBar/SearchBar";
// import Table from "../components/Table/Table";

// const Search = () => {
//   const [headers, setHeaders] = useState([]);
//   const [filteredData, setFilteredData] = useState([]);
//   const [query, setQuery] = useState("");
//   const [imageUrl, setImageUrl] = useState(null);
//   const [selectedOption, setSelectedOption] = useState("module");
//   const [isLoading, setIsLoading] = useState(false);
//   const [errorMessage, setErrorMessage] = useState(null);
//   const baseURL = process.env.REACT_APP_API_BASE_URL;

//   useEffect(() => {
//     setHeaders([
//       { dbParam: "serial_number", displayName: "S.No" }, // Add serial number header
//       { dbParam: "supplier_name", displayName: "Supplier Name" },
//       { dbParam: "Pmax", displayName: "Pmax" },
//       { dbParam: "Vmp", displayName: "Vmpp" },
//       { dbParam: "Imp", displayName: "Imp" },
//       { dbParam: "Voc", displayName: "Voc" },
//       { dbParam: "Isc", displayName: "Isc" },
//       { dbParam: "Junction Box", displayName: "Junction Box" },
//       { dbParam: "Line", displayName: "Line Code" },
//       { dbParam: "FF", displayName: "FF" },
//       { dbParam: "EL_Upload", displayName: "EL Upload (Yes/No)" },
//       { dbParam: "Flash_Date", displayName: "Flash Date" },
//       { dbParam: "Module_No", displayName: "Module Serial No" },
//       { dbParam: "Model", displayName: "Model" },
//       { dbParam: "Pallet_No", displayName: "Pallet No" },
//       { dbParam: "Container_No", displayName: "Container No" },
//       { dbParam: "Truck_No", displayName: "Truck No" },
//       { dbParam: "Location", displayName: "Storage Location" },
//       { dbParam: "Binning", displayName: "Binning" },
//       { dbParam: "Efficiency (%)", displayName: "Efficiency (%)" },
//       { dbParam: "project_id", displayName: "Project ID" },
//       { dbParam: "updated_time", displayName: "Updated At" },
//       { dbParam: "updated_by", displayName: "Updated By" },
//     ]);
//   }, []);

//   const handleSearch = async (option, query) => {
//     setSelectedOption(option);
//     setIsLoading(true);
//     setErrorMessage(null);
//     try {
//       const searchType = option === "module" ? "module_sr_no" : option === "pallet" ? "pallet_no" : "container_no";
//       const selectedProject = localStorage.getItem("selectedProject");

//       const tableResponse = await axios.post(
//         `${baseURL}search`,
//         {
//           project_id: selectedProject,
//           search_type: searchType,
//           search_value: query,
//         }
//       );

//       let extractedData = [];
//       if (tableResponse.data.status === "success") {
//         if (tableResponse.data.documents) {
//           extractedData = tableResponse.data.documents.map((doc) => doc.data);
//         } else if (tableResponse.data.data) {
//           extractedData = tableResponse.data.data;
//         }
//         // Transform null values to "N/A" and add serial number
//         extractedData = extractedData.map((item, index) => ({
//           ...item,
//           serial_number: index + 1, // Add serial number (1-based index)
//           ...Object.keys(item).reduce((acc, key) => {
//             acc[key] = item[key] === null ? "N/A" : item[key];
//             return acc;
//           }, {}),
//         }));
//       } else {
//         setErrorMessage(tableResponse.data.message || "No data found for the search.");
//       }
//       setFilteredData(extractedData);

//       try {
//         const response = await axios.post(
//           `${baseURL}download_el_images`,
//           {
//             project_id: selectedProject,
//             search_type: searchType,
//             search_value: query,
//           },
//           { responseType: "arraybuffer" }
//         );

//         if (searchType === "module_sr_no") {
//           const base64Image = `data:image/png;base64,${btoa(
//             new Uint8Array(response.data).reduce(
//               (data, byte) => data + String.fromCharCode(byte),
//               ""
//             )
//           )}`;
//           setImageUrl(base64Image);
//         } else if (searchType === "pallet_no") {
//           const blob = new Blob([response.data], { type: "application/zip" });
//           const zipUrl = URL.createObjectURL(blob);
//           setImageUrl(zipUrl);
//         }
//       } catch (imageError) {
//         console.warn("No image or zip found for this search:", imageError);
//         setImageUrl(null);
//       }

//       setQuery(query);
//     } catch (error) {
//       console.error("Error fetching data or image:", error);
//       setFilteredData([]);
//       setImageUrl(null);
//       setErrorMessage(error.response?.data?.message || "An error occurred while fetching data.");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleSort = (columnKey) => {
//     if (columnKey === "serial_number") return; // Prevent sorting on serial number
//     const sortedData = [...filteredData].sort((a, b) => {
//       const aValue = a[columnKey] === "N/A" ? "" : a[columnKey];
//       const bValue = b[columnKey] === "N/A" ? "" : b[columnKey];
//       if (aValue < bValue) return -1;
//       if (aValue > bValue) return 1;
//       return 0;
//     });
//     // Reassign serial numbers after sorting
//     const updatedData = sortedData.map((item, index) => ({
//       ...item,
//       serial_number: index + 1,
//     }));
//     setFilteredData(updatedData);
//   };

//   const handleDownloadImage = () => {
//     if (imageUrl) {
//       const link = document.createElement("a");
//       link.href = imageUrl;
//       link.download =
//         selectedOption === "module" ? `image_${query}.png` : `pallet_${query}.zip`;
//       link.click();
//     }
//   };

//   return (
//     <div className="mt-5 space-y-10">
//       <div className="w-full text-center border-b-2 pb-2">
//         <div className="font-semibold" style={{ color: "#808080" }}>
//           Traceability (Sr #/ pallet #/ Container)
//         </div>
//       </div>
//       <SearchBar onSearch={handleSearch} />
//       {isLoading ? (
//         <div className="flex flex-col justify-center items-center h-64">
//           <img
//             src="/loader.gif"
//             alt="Loading..."
//             className="w-28 h-28"
//             onError={() => console.error("Failed to load GIF at /loader.gif")}
//           />
//           <p className="text-gray-500 text-sm mt-2">Loading data...</p>
//         </div>
//       ) : (
//         <>
//           {errorMessage && (
//             <div className="flex justify-center items-center mb-5">
//               <div className="flex items-center bg-red-100 px-4 py-2 rounded-lg shadow-md">
//                 <svg
//                   className="w-5 h-5 text-red-500 mr-2"
//                   fill="none"
//                   stroke="currentColor"
//                   viewBox="0 0 24 24"
//                   xmlns="http://www.w3.org/2000/svg"
//                 >
//                   <circle cx="12" cy="12" r="10" strokeWidth="2" />
//                   <path d="M12 8v4" strokeWidth="2" strokeLinecap="round" />
//                   <circle cx="12" cy="16" r="1" fill="currentColor" />
//                 </svg>
//                 <p className="text-red-500 italic">{errorMessage}</p>
//               </div>
//             </div>
//           )}
//           {imageUrl ? (
//             <div className="flex flex-col items-center mb-5">
//               {selectedOption === "module" ? (
//                 <img
//                   src={imageUrl}
//                   alt="Search Result"
//                   className="max-w-full h-auto rounded-lg shadow-md"
//                 />
//               ) : selectedOption === "pallet" ? (
//                 <p className="text-gray-500 italic">Zip file ready for download.</p>
//               ) : null}
//               {selectedOption !== "container" && (
//                 <button
//                   onClick={handleDownloadImage}
//                   className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
//                 >
//                   Download {selectedOption === "module" ? "Image" : "Zip"}
//                 </button>
//               )}
//             </div>
//           ) : (
//             query &&
//             !errorMessage &&
//             selectedOption !== "container" && (
//               <div className="flex justify-center items-center mb-5">
//                 <p className="text-gray-500 italic">
//                   No images or zip files found for this search.
//                 </p>
//               </div>
//             )
//           )}
//           {filteredData.length === 0 && query && !errorMessage && (
//             <div className="flex justify-center items-center mb-5">
//               <p className="text-red-500 italic">
//                 No data found for the current {selectedOption === "module" ? "Module Serial No" : selectedOption === "pallet" ? "Pallet No" : "Container No"}.
//               </p>
//             </div>
//           )}
//           <Table
//             tableHeaders={headers}
//             tableData={filteredData}
//             onSort={handleSort}
//           />
//         </>
//       )}
//     </div>
//   );
// };

// export default Search;

